"use client"

import * as React from "react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  getContactAvatarInitials,
  getVercelAvatarKey,
  getVercelAvatarUrl,
  normalizeProfilePicUrl,
} from "@/lib/resolveUserAvatar"

export interface ContactAvatarProps {
  profilePicUrl?: string | null
  email?: string
  userId?: string
  displayName: string
  className?: string
}

/**
 * Contact avatar: try Meta `profile_pic_url` first; if it fails to load, use Vercel gradient;
 * Radix AvatarFallback shows initials when Vercel also fails.
 *
 * Note: Radix AvatarImage does not render an `<img>` on load error, so `onError` on AvatarImage
 * never runs. We probe the primary URL with `Image()` and only then pass a single `src` to Radix.
 */
export function ContactAvatar({
  profilePicUrl,
  email = "",
  userId = "",
  displayName,
  className,
}: ContactAvatarProps) {
  const vercelKey = getVercelAvatarKey(email, userId, displayName)
  const vercelUrl = getVercelAvatarUrl(vercelKey)
  const primary = normalizeProfilePicUrl(profilePicUrl)

  const [displaySrc, setDisplaySrc] = React.useState(() => primary ?? vercelUrl)

  React.useEffect(() => {
    if (!primary) {
      setDisplaySrc(vercelUrl)
      return
    }
    setDisplaySrc(primary)
    const img = new window.Image()
    let cancelled = false
    img.onload = () => {
      if (!cancelled) setDisplaySrc(primary)
    }
    img.onerror = () => {
      if (!cancelled) setDisplaySrc(vercelUrl)
    }
    img.src = primary
    return () => {
      cancelled = true
      img.onload = null
      img.onerror = null
    }
  }, [primary, vercelUrl])

  const initials = getContactAvatarInitials(displayName)

  return (
    <Avatar className={className}>
      <AvatarImage src={displaySrc} alt={displayName} />
      <AvatarFallback>{initials}</AvatarFallback>
    </Avatar>
  )
}
